<antm-page-title title="External Calls Tracker" subtitle="Monitor outbound HTTP integrations, response statuses, GPRS sat pings, and webhook callbacks
        across Dakar Express microservices." />
<div class="ec-page">

  <!-- ── Statistiques ────────────────────────────────────────────────────── -->
  @if (stats(); as s) {
    <div class="ec-stats-grid">

      <!-- Total -->
      <mat-card class="ec-stat-card">
        <mat-card-content>
          <div class="ec-stat-card__header">
            <p class="ec-stat-card__label">Total Calls Recorded</p>
            <div class="ec-stat-card__icon-wrap">
              <mat-icon>description</mat-icon>
            </div>
          </div>
          <div class="ec-stat-card__value-row">
            <span class="ec-stat-card__value">{{ s.total }}</span>
            <span class="ec-stat-card__unit">entries</span>
          </div>
          <p class="ec-stat-card__sublabel">Aggregated active log streams</p>
        </mat-card-content>
      </mat-card>

      <!-- Taux de succès -->
      <mat-card class="ec-stat-card">
        <mat-card-content>
          <div class="ec-stat-card__header">
            <p class="ec-stat-card__label">Gateway Success Rate</p>
            <span class="ec-live-dot ec-live-dot--green"></span>
          </div>
          <div class="ec-stat-card__value-row">
            <span
              class="ec-stat-card__value"
              [class.ec-stat-card__value--success]="s.successRate >= 90"
              [class.ec-stat-card__value--warn]="s.successRate < 90">
              {{ s.successRate }}%
            </span>
            <span class="ec-stat-card__unit">completed</span>
          </div>
          <div class="ec-success-bar">
            <div class="ec-success-bar__fill" [style.width.%]="s.successRate"></div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Latence moyenne -->
      <mat-card class="ec-stat-card">
        <mat-card-content>
          <div class="ec-stat-card__header">
            <p class="ec-stat-card__label">Average API Latency</p>
            <div class="ec-stat-card__icon-wrap">
              <mat-icon>schedule</mat-icon>
            </div>
          </div>
          <div class="ec-stat-card__value-row">
            <span
              class="ec-stat-card__value"
              [class.ec-stat-card__value--success]="s.averageDuration < 250"
              [class.ec-stat-card__value--warn]="s.averageDuration >= 250 && s.averageDuration < 600"
              [class.ec-stat-card__value--error]="s.averageDuration >= 600">
              {{ s.averageDuration }}<span class="ec-stat-card__unit--inline">ms</span>
            </span>
          </div>
          <p class="ec-stat-card__sublabel">Target threshold limit: 500ms</p>
        </mat-card-content>
      </mat-card>

      <!-- Erreurs -->
      <mat-card class="ec-stat-card" [class.ec-stat-card--error]="s.failures > 0">
        <mat-card-content>
          <div class="ec-stat-card__header">
            <p class="ec-stat-card__label">Unresolved Errors</p>
            <div class="ec-stat-card__icon-wrap" [class.ec-stat-card__icon-wrap--error]="s.errorCount > 0">
              <mat-icon>error_outline</mat-icon>
            </div>
          </div>
          <div class="ec-stat-card__value-row">
            <span
              class="ec-stat-card__value"
              [class.ec-stat-card__value--error]="s.errorCount > 0">
              {{ s.errorCount }}
            </span>
            <span class="ec-stat-card__unit">exceptions</span>
          </div>
          <p class="ec-stat-card__sublabel">Requires administrator intervention</p>
        </mat-card-content>
      </mat-card>

    </div>
  }

  <!-- ── Panneau de logs ─────────────────────────────────────────────────── -->
  <mat-card class="ec-log-panel">

    <!-- Filtres -->
    <div class="ec-log-filters">
      <form [formGroup]="filterForm" class="ec-log-filters__form">

        <mat-form-field appearance="outline" class="ec-log-filters__field">
          <mat-label>Rechercher...</mat-label>
          <input matInput formControlName="search" placeholder="Search API reference, IP, route..." />
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <mat-form-field appearance="outline" class="ec-log-filters__field">
          <mat-label>Service Category</mat-label>
          <mat-select formControlName="type">
            <mat-option value="ALL">All Service Categories</mat-option>
            <mat-option value="ORANGE_MONEY_CM">Orange Money Cameroun</mat-option>
            <mat-option value="MTN_CM">MTN Cameroun</mat-option>
            <mat-option value="PAYMENT">💳 Payments (Wave, Orange Money)</mat-option>
            <mat-option value="SMS">💬 SMS Outbound Gateways</mat-option>
            <mat-option value="GPS">🛰️ GPS Fleet Tracker API</mat-option>
            <mat-option value="MAPS">🗺️ Google Maps Partner Routing</mat-option>
            <mat-option value="VERIFICATION">👤 Driver license authentication</mat-option>
            <mat-option value="EMAIL">✉️ SendGrid Receipt Mailers</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="ec-log-filters__field">
          <mat-label>Network Status</mat-label>
          <mat-select formControlName="status">
            <mat-option value="ALL">All Network Statuses</mat-option>
            <mat-option value="SUCCESS">🟢 SUCCESS (2xx OK)</mat-option>
            <mat-option value="FAILED">🔴 FAILED (Errors)</mat-option>
            <mat-option value="RETRYING">🟡 RETRYING</mat-option>
          </mat-select>
        </mat-form-field>

      </form>
    </div>

    <!-- Vue partagée liste / détail -->
    <div class="ec-log-split">

      <!-- Liste des appels -->
      <div class="ec-log-list">
        @if (filteredCalls().length === 0) {
          <div class="ec-log-empty">
            <mat-icon class="ec-log-empty__icon">sentiment_dissatisfied</mat-icon>
            <p class="ec-log-empty__title">No telemetry log matching criteria</p>
            <p class="ec-log-empty__subtitle">
              Adjust search patterns or try pushing action buttons in the simulator ribbon above.
            </p>
          </div>
        } @else {
          @for (call of externalCalls(); track call.id) {
            <div
              class="ec-log-entry"
              [class.ec-log-entry--selected]="selectedCall()?.id === call.id"
              (click)="selectCall(call)">

              <div
                class="ec-log-entry__dot"
                [class.ec-log-entry__dot--success]="call.httpStatus >= 200 && call.httpStatus < 400"
                [class.ec-log-entry__dot--failed]="call.httpStatus >= 400">
              </div>

              <div class="ec-log-entry__body">
                <div class="ec-log-entry__top">
                  <span class="ec-log-entry__id">{{ call.id }}</span>
                  <span class="ec-log-entry__timestamp">{{ call.calledAt | date:'medium' }}</span>
                </div>

                <div class="ec-log-entry__middle">
                  <div>
                    <h4 class="ec-log-entry__service-name">{{ call.provider }}</h4>
                    <p class="ec-log-entry__endpoint">
                      <span class="ec-log-entry__method">{{ call.httpMethod }}</span> {{ call.route }}
                    </p>
                  </div>
                  <div class="ec-log-entry__meta">
                    <span [class]="getStatusClass(getStatus(call))">{{ call.httpStatus }}</span>
                    <p [class]="getLatencyClass(call.durationMs)">{{ call.durationMs }}ms</p>
                  </div>
                </div>

                @if (call.errorMessage) {
                  <p class="ec-log-entry__error">
                    <span class="ec-log-entry__error-label">Error Response:</span>
                    {{ call.errorMessage }}
                  </p>
                }

                @if (call.affectedEntity) {
                  <div class="ec-log-entry__entity">
                    <mat-icon class="ec-log-entry__entity-icon">link</mat-icon>
                    Linked resource:
                    <span class="ec-log-entry__entity-name">
                      {{ call.affectedEntity.type }} ({{ call.affectedEntity.id }})
                    </span>
                  </div>
                }
              </div>
            </div>
          }
          @for (call of filteredCalls(); track call.id) {
            <div
              class="ec-log-entry"
              [class.ec-log-entry--selected]="selectedCall()?.id === call.id"
              (click)="selectCall(call)">

              <div
                class="ec-log-entry__dot"
                [class.ec-log-entry__dot--success]="call.status === 'SUCCESS'"
                [class.ec-log-entry__dot--failed]="call.status !== 'SUCCESS'">
              </div>

              <div class="ec-log-entry__body">
                <div class="ec-log-entry__top">
                  <span class="ec-log-entry__id">{{ call.id }}</span>
                  <span class="ec-log-entry__timestamp">{{ call.timestamp | date:'medium' }}</span>
                </div>

                <div class="ec-log-entry__middle">
                  <div>
                    <h4 class="ec-log-entry__service-name">{{ call.serviceName }}</h4>
                    <p class="ec-log-entry__endpoint">
                      <span class="ec-log-entry__method">{{ call.httpMethod }}</span> {{ call.route }}
                    </p>
                  </div>
                  <div class="ec-log-entry__meta">
                    <span [class]="getStatusClass(call.status)">{{ call.httpStatus }}</span>
                    <p [class]="getLatencyClass(call.durationMs)">{{ call.durationMs }}ms</p>
                  </div>
                </div>

                @if (call.errorMessage) {
                  <p class="ec-log-entry__error">
                    <span class="ec-log-entry__error-label">Error Response:</span>
                    {{ call.errorMessage }}
                  </p>
                }

                @if (call.affectedEntity) {
                  <div class="ec-log-entry__entity">
                    <mat-icon class="ec-log-entry__entity-icon">link</mat-icon>
                    Linked resource:
                    <span class="ec-log-entry__entity-name">
                      {{ call.affectedEntity.type }} ({{ call.affectedEntity.id }})
                    </span>
                  </div>
                }
              </div>
            </div>
          }
        }
      </div>

      <!-- Panneau de détail -->
      <div class="ec-log-detail">
        @if (selectedCall(); as call) {
          <div class="ec-detail">

            <div class="ec-detail__header">
              <div>
                <span class="ec-detail__trace-label">TELEMETRY DETAILED TRACE</span>
                <h3 class="ec-detail__title">{{ call.id }} — Service Trace</h3>
                <p class="ec-detail__timestamp">{{ call.timestamp | date:'long' }}</p>
              </div>
              <button mat-icon-button (click)="closeDrawer()">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <!-- Type & statut HTTP -->
            <div class="ec-detail__stats">
              <div class="ec-detail__stat-item">
                <p class="ec-detail__stat-label">Protocol Type</p>
                <span [class]="getTypeBadgeClass(call.provider)">{{ call.provider }}</span>
              </div>
              <div class="ec-detail__stat-item">
                <p class="ec-detail__stat-label">HTTP Status Code</p>
                <span [class]="getStatusClass(getStatus(call))">HTTP {{ call.httpStatus }}</span>
              </div>
            </div>

            <!-- Entité liée -->
            @if (call.affectedEntity) {
              <div class="ec-detail__entity-link">
                <div>
                  <p class="ec-detail__entity-link-label">Linked Application Entity</p>
                  <p class="ec-detail__entity-link-text">
                    This call is linked with: <strong>{{ call.affectedEntity.label }}</strong>
                  </p>
                </div>
                <a
                  mat-raised-button
                  color="primary"
                  [routerLink]="getFormatEntityRoute(call.affectedEntity.type, call.affectedEntity.id)">
                  Go to Resource
                </a>
              </div>
            }

            <!-- Payloads -->
            <div class="ec-detail__payload-header">
              <span class="ec-detail__payload-title">HTTP Payloads</span>
              <button mat-button color="primary" (click)="showPayloadRaw.set(!showPayloadRaw())">
                {{ showPayloadRaw() ? 'Format JSON Code' : 'Raw View' }}
              </button>
            </div>

            <div class="ec-detail__payload-section">
              <p class="ec-detail__payload-label">Request Payload &amp; Headers</p>
              @if (showPayloadRaw()) {
                <pre class="ec-payload-pre ec-payload-pre--raw">Method: {{ call.httpMethod }}
                  Headers: {{ call.requestHeader | json }}

                  {{ call.requestPayload }}</pre>
              } @else {
                <pre class="ec-payload-pre ec-payload-pre--request">{{ call.requestPayload }}</pre>
              }
            </div>

            <div class="ec-detail__payload-section">
              <p class="ec-detail__payload-label">Response Payload</p>
              @if (showPayloadRaw()) {
                <pre class="ec-payload-pre ec-payload-pre--raw">Headers: {{ call.responseHeader | json }}

                  {{ call.responsePayload }}</pre>
              } @else {
                <pre class="ec-payload-pre ec-payload-pre--response">{{ call.responsePayload }}</pre>
              }
            </div>
          </div>
        } @else {
          <div class="ec-detail-empty">
            <div class="ec-detail-empty__icon-wrap">
              <mat-icon class="ec-detail-empty__icon">smartphone</mat-icon>
            </div>
            <h4 class="ec-detail-empty__title">No telemetry trace selected</h4>
            <p class="ec-detail-empty__subtitle">
              Click on any integration call log entry from the list to display headers, JSON payloads,
              and linked systems.
            </p>
          </div>
        }
      </div>

    </div>
  </mat-card>

</div>
